<script setup lang="ts">
import DirectMessagesNavigation from "@/v2Projects/components/DirectMessagesNavigation.vue";
import { ref, computed } from "vue";
import { useRoute, useRouter } from 'vue-router'
import SideNavigation from "../components/SideNavigation.vue";
import TopNavigation from "../components/expert/TopNavigation.vue";
import {useAuthStore} from "@/store/auth.ts";
import {useExpertStore} from "@/store/expert.ts";
import ListingSideNav from "@/components/expert/listing-settings/ListingSideNav.vue";
import { UiToaster } from "@shopexperts/shared";
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";
import NotificationToastListener from "@/v2Projects/components/NotificationToastListener.vue";
import NotificationToastBody from "@/v2Projects/components/NotificationToastBody.vue";

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const expertStore = useExpertStore()
const { isEnabled } = useFeatureFlags();

const navItems = computed(() => {
  const items = [
    { label: 'Overview', icon: 'overview', path: '/expert/dashboard' },
  ];
  if (isEnabled('enable_v2_projects')) {
    items.push({ label: 'Leads & Projects', icon: 'leads', path: '/expert/projects' });
    items.push({ label: 'Messages', icon: 'chat-bubble', path: '/expert/messages' });
  } else {
    items.push({ label: 'Leads', icon: 'leads', path: '/expert/leads' });
  }
  items.push(
    { label: 'My Listing', icon: 'listing', path: '/expert/listing' },
    { label: 'Reviews', icon: 'reviews', path: '/expert/reviews' },
  );
  return items;
});

const mobileMenuItems = [
  { label: 'Settings', icon: 'settings', path: '/expert/settings' },
]

const isMobileMenuOpen = ref(false);
const expertUser = ref<any>(null)
const expertUserLoading = ref(false)

// Fetch expert settings data when mobile menu opens
const fetchExpertSettings = async () => {
  try {
    expertUserLoading.value = true
    const response = await expertStore.fetchExpertProfile('');
    expertUser.value = response.expert;
    
    // Update auth store user with fresh data if needed
    if (response.expert) {
      authStore.user = { ...authStore.user, ...expertUser.value }
    }
  } catch (error) {
    console.error('Failed to fetch expert settings:', error)
    // Fallback to auth store user if API fails
    expertUser.value = authStore.user
  } finally {
    expertUserLoading.value = false
  }
}

// Watch for mobile menu opening to fetch data
const onMobileMenuChange = (isOpen: boolean | undefined) => {
  if (isOpen && !expertUser.value) {
    fetchExpertSettings()
  }
}

// Todo(khaled): Kill this mess after merging the listing editor
const listingRoutes = [
  '/expert/listing-settings',
  '/expert/listing-settings/personal-details',
  '/expert/listing-settings/service-categories',
  '/expert/listing-settings/packaged-services',
  '/expert/listing-settings/customer-stories',
  '/expert/listing-settings/faq'
]

const isListingSettings = listingRoutes.includes(route.path)

const handleLogout = async () => {
  authStore.logout();
  await router.push('/login');
}

const handleSwitchDashboard = async () => {
  try {
    const { data } = await authStore.switchToOldDashboard();
    window.location.href = data.url;
  } catch (err) {
    console.error('SSO switch failed', err);
  }
}
</script>


<template>
  <UiToaster v-if="isEnabled('enable_v2_projects')" placement="top-end" top-offset="4.75rem">
    <template #toast="{ toast }">
      <NotificationToastBody :toast="toast" />
    </template>
    <NotificationToastListener />
  </UiToaster>
  <div class="flex h-screen overflow-hidden flex-col bg-muted">
      <TopNavigation
        v-model:mobileMenuOpen="isMobileMenuOpen"
        :is-listing-settings="isListingSettings"
      />

      <div class="flex flex-1 min-h-0">
        <ListingSideNav
          v-if="isListingSettings"
          class="w-[20%]"
        />
        <SideNavigation
          v-else
          class="shrink-0 xl:w-56 2xl:w-60"
          :menu-items="navItems"
          :mobile-menu-items="mobileMenuItems"
          mobile-logout-icon="logout"
          :is-expert-side-nav="true"
          :compact="true"
          :expert-user="expertUser || authStore.user"
          :expert-user-loading="expertUserLoading"
          v-model:mobileMenuOpen="isMobileMenuOpen"
          @logout="handleLogout"
          @switch-dashboard="handleSwitchDashboard"
          @update:mobileMenuOpen="onMobileMenuChange"
        >
          <template #nav-messages="{ close }">
            <DirectMessagesNavigation side="expert" @navigate="close" />
          </template>
        </SideNavigation>
        <div class="flex-1 min-w-0 min-h-0 flex">
          <RouterView />
        </div>
      </div>
  </div>
</template>

<style scoped>

</style>
